import { Shield } from "lucide-react";
import React from "react";
import { FaLightbulb, FaTrophy, FaUsers, FaBook, FaChartLine, FaGraduationCap, FaHeart, FaRocket,  } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const AboutPage = () => {
    const navigate = useNavigate();

    const stats = [
        { number: "1,000+", label: "Active Students", icon: FaUsers },
        { number: "15+", label: "Subjects Covered", icon: FaBook },
        { number: "500+", label: "Past Questions", icon: FaGraduationCap },
        { number: "95%", label: "Improvement Rate", icon: FaChartLine }
    ];

    const teamValues = [
        {
            icon: FaHeart,
            title: "Student-First",
            description: "Every decision we make puts student success at the center."
        },
        {
            icon: FaRocket,
            title: "Innovation",
            description: "We constantly evolve our platform with cutting-edge learning tools."
        },
        {
            icon: Shield,
            title: "Quality Assurance",
            description: "All our content is verified by experienced educators and exam experts."
        }
    ];

    return (
        <div className="bg-black/50">
            {/* Hero Section */}
            <section
                className="py-24 pt-32 text-white relative overflow-hidden"
                style={{
                    backgroundImage: "url('https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80')",
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
            >
                {/* Dark blue overlay */}
                <div className="absolute inset-0 bg-black/65 bg-opacity-75"></div>

                {/* Content */}
                <div className="container mx-auto px-6 text-center relative z-10">
                    <h1 className="text-5xl md:text-6xl font-extrabold mb-6">
                        About <span className="text-blue-200">TeeChaa</span>
                    </h1>
                    <p className="text-xl md:text-2xl max-w-4xl mx-auto mb-8 leading-relaxed opacity-90">
                        Empowering Nigerian students to achieve academic excellence through
                        innovative digital learning solutions
                    </p>
                    <div className="w-24 h-1 bg-blue-200 mx-auto rounded"></div>
                </div>
            </section>

            {/* Our Story Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="text-4xl font-bold text-center mb-12 text-gray-800">Our Story</h2>
                        <div className="grid md:grid-cols-2 gap-12 items-center">
                            <div
                                className="rounded-2xl bg-cover bg-center h-96 shadow-lg"
                                style={{
                                    backgroundImage: 'url("https://res.cloudinary.com/dba1aezsn/image/upload/v1762792424/Lebo_wants_to_be_a_chef__She_loves_to_cook_Most_skuljy.jpg")'
                                }}
                            ></div>
                            <div>
                                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                                    TeeChaa was born from a simple observation: too many brilliant Nigerian students
                                    were struggling not because they lacked ability, but because they lacked access
                                    to quality study resources.
                                </p>
                                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                                    Founded in 2023, we set out to bridge this gap by creating Nigeria's most
                                    comprehensive digital learning platform. Today, we're proud to serve over
                                    10,000 students across the country.
                                </p>
                                <div className="bg-blue-50 p-6 rounded-xl">
                                    <p className="text-[#2148C0] font-semibold italic">
                                        "Every student deserves the opportunity to excel, regardless of their
                                        background or location."
                                    </p>
                                    <p className="text-sm text-gray-600 mt-2">- TeeChaa Founders</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <h2 className="text-4xl font-bold text-center mb-12 text-gray-800">TeeChaa by the Numbers</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-all">
                                <stat.icon className="text-4xl text-[#2148C0] mx-auto mb-4" />
                                <div className="text-3xl font-bold text-gray-800 mb-2">{stat.number}</div>
                                <div className="text-gray-600 font-medium">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Mission, Vision, Values */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <h2 className="text-4xl font-bold text-center mb-16 text-gray-800">What Drives Us</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow-md hover:shadow-lg transition-all transform hover:-translate-y-2">
                            <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                                <FaLightbulb className="text-3xl text-[#2148C0]" />
                            </div>
                            <h3 className="text-2xl font-semibold mb-4 text-center">Our Mission</h3>
                            <p className="text-gray-600 text-center leading-relaxed">
                                To democratize quality education by providing affordable, accessible, and engaging
                                study resources for every student in Nigeria.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow-md hover:shadow-lg transition-all transform hover:-translate-y-2">
                            <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                                <FaTrophy className="text-3xl text-[#2148C0]" />
                            </div>
                            <h3 className="text-2xl font-semibold mb-4 text-center">Our Vision</h3>
                            <p className="text-gray-600 text-center leading-relaxed">
                                To be Africa's most trusted platform for exam preparation, empowering millions
                                of learners to achieve academic excellence.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 shadow-md hover:shadow-lg transition-all transform hover:-translate-y-2">
                            <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                                <FaUsers className="text-3xl text-[#2148C0]" />
                            </div>
                            <h3 className="text-2xl font-semibold mb-4 text-center">Our Values</h3>
                            <p className="text-gray-600 text-center leading-relaxed">
                                We stand for <span className="font-medium text-[#2148C0]">excellence, innovation,
                                    accessibility, and student success</span> — guiding everything we do.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* What Sets Us Apart */}
            <section
                className="py-24 bg-cover bg-center bg-fixed relative"
                style={{
                    backgroundImage: 'url("https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")'
                }}
            >
                <div className="absolute inset-0 bg-black bg-opacity-70"></div>
                <div className="container mx-auto px-6 relative z-10">
                    <h2 className="text-4xl font-bold text-center mb-16 text-white">What Sets Us Apart</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {teamValues.map((value, index) => (
                            <div key={index} className="text-center p-8 bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl border border-white border-opacity-20">
                                <value.icon className="text-4xl text-blue-200 mx-auto mb-6" />
                                <h3 className="text-xl font-semibold text-white mb-4">{value.title}</h3>
                                <p className="text-blue-100 leading-relaxed">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Commitment */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-6 text-center">
                    <h2 className="text-4xl font-bold mb-8 text-gray-800">Our Commitment to You</h2>
                    <div className="max-w-4xl mx-auto">
                        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                            We understand that every student's journey is unique. That's why we've built TeeChaa
                            to be more than just a study platform — we're your academic partner, committed to
                            supporting you every step of the way.
                        </p>
                        <div className="grid md:grid-cols-2 gap-8 mt-12">
                            <div className="bg-white p-6 rounded-xl shadow-md">
                                <h4 className="font-semibold text-[#2148C0] mb-3">✓ Always Free Core Features</h4>
                                <p className="text-gray-600">Essential study materials and basic quizzes will always remain free.</p>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-md">
                                <h4 className="font-semibold text-[#2148C0] mb-3">✓ Regular Content Updates</h4>
                                <p className="text-gray-600">Fresh questions and materials added every week to keep you current.</p>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-md">
                                <h4 className="font-semibold text-[#2148C0] mb-3">✓ Expert-Verified Content</h4>
                                <p className="text-gray-600">All materials reviewed by experienced teachers and exam experts.</p>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-md">
                                <h4 className="font-semibold text-[#2148C0] mb-3">✓ 24/7 Support</h4>
                                <p className="text-gray-600">Get help whenever you need it through our support channels.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-[#2148C0] text-white text-center">
                <div className="container mx-auto px-6">
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">
                        Ready to join the TeeChaa family?
                    </h2>
                    <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
                        Join thousands of students who are already transforming their academic journey with TeeChaa.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        {/* <button
                            onClick={() => navigate("/signup")}
                            className="bg-white text-[#2148C0] px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105"
                        >
                            Get Started Free
                        </button> */}
                        <button
                            onClick={() => navigate("/contact-us")}
                            className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-[#2148C0] transition-all"
                        >
                            Contact Us
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;